import * as React from "react";
import * as _ from "lodash";
import EditParameter from "../../Editing/EditParameter";
import Table from "../../../components/Table/Table";
import { Dimmer } from "semantic-ui-react";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";
import { getCategoryPathForParameter } from "../../../utils/pdApiUtils";
import { editActions } from "../../../actions/editActions";
import { pdApiActions } from "../../../actions/pdApiActions";
import { mainActions } from "../../../actions/mainActions";
import { getDependentGroupParametersColumns } from "../../../reducers/parameterTableConfig";

/**
 * All the following props are passed from parent component
 */
export type GroupParametersTableProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  isLoadingParameters: boolean;
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
  mainActions: typeof mainActions;
  subMenuRoute: State.GroupSubmenu;
  toggleModal: () => void;
  columnFilters: Filter[];
  setColumnFilters: (subMenuRoute: State.GroupSubmenu, columnFilter: Filter[]) => void;
};

type GroupParametersTableState = {
  isModalOpen: boolean;
  selectedDomain: Api.Domain;
};

export const setGroupParameterExtraProperties = (
  parameter: Api.GroupParameter,
  pdApi: State.PdApi
): Api.AnyParameter => {
  const { domains, domainGroups, parameterCategories } = pdApi;
  const groupId = domains[parameter.domain.id].domainGroup.id;
  const domainName = domains[parameter.domain.id].enumName + " [" + domains[parameter.domain.id].enumValue+ "]";
  const domainGroupName = domainGroups[groupId].enumName;
  let categoryName = "";
  let categoryPath = "";
  if (parameter.parameterCategory != null) {
    categoryName = parameterCategories[parameter.parameterCategory.id].name;
    const parameterCategoryList = _.values(parameterCategories).filter(
      category => category.domainGroup.id === groupId
    );
    categoryPath = getCategoryPathForParameter(parameter, parameterCategoryList, false);
  }
  const feature = pdApi.features[parameter.featureDbId].swName;
  const modifiedParameter = {
    ...parameter,
    feature,
    commonExtra: {
      domainGroupName,
      domainName,
      categoryName,
      categoryPath
    }
  };
  return modifiedParameter;
};

export class GroupParametersTable extends React.Component<
  GroupParametersTableProps,
  GroupParametersTableState
> {
  state = {
    isModalOpen: false,
    selectedDomain: {} as Api.Domain
  };

  componentWillUnmount(): void {
    this.props.mainActions.setLastEditedObjectId(undefined);
  }

  componentDidMount(): void {
    // make throttle function
    this.handleFilterChangeDebounced = _.debounce(this.handleFilterChangeDebounced, 500);
  }

  private handleFilterChangeDebounced = (changedFilters: Filter[]) => {
    this.props.setColumnFilters(this.props.subMenuRoute, changedFilters);
  };

  render(): JSX.Element {
    const {
      columns,
      pdApi,
      mainState,
      isEditActive,
      toggleModal,
      isLoadingParameters
    } = this.props;
    const { groupParameters } = pdApi;
    const { editSize, pageSize, isShortenTextEnabled } = mainState;
    let parameterRows: Api.AnyParameter[] = [];
    let selectedColumns: State.Column[] = [];
    parameterRows = groupParameters.map(row => setGroupParameterExtraProperties(row, pdApi));

    // Update feature filter options
    const filteredGroupParameters: Api.GroupParameter[] = !this.state.selectedDomain?.id
      ? groupParameters
      : groupParameters.filter(param => param.domain.id === this.state.selectedDomain.id);

    const featureDbIds: number[] = filteredGroupParameters.map(param => param.featureDbId);
    const featureOptions: string[] = _.uniq(featureDbIds.map(id => pdApi.features[id].swName));

    // Note: dropdown filter options are set in Columns, value is set in Table ('filtered' property)
    selectedColumns = getDependentGroupParametersColumns(
      columns["Group Parameters"],
      featureOptions
    );

    const startingPage = mainState.lastEditedObjectId
      ? (parameterRows.findIndex(parameter => parameter.id === mainState.lastEditedObjectId) / // tslint:disable-line: no-bitwise
          mainState.pageSize) >>
        0
      : 0;
    // Add submenu specific columns and rows
    return (
      <div className="flex-1">
        <div className="flex-1">
          <Table
            startingPage={startingPage}
            rows={parameterRows}
            loading={isLoadingParameters}
            loadingText="Fetching group parameters..."
            columns={selectedColumns}
            isHidingTextActive={isShortenTextEnabled}
            handleRowSelection={this.props.editActions.select}
            isRowSelected={this.isParameterSelected}
            pageSize={pageSize}
            handleFilterChange={this.handleFilterChangeDebounced}
          />
        </div>
        {isEditActive && (
          <Dimmer active={true} page={true}>
            <div className="edit-view" style={{ flex: editSize , width : editSize == 0.3 ? "500px" : "1000px" }}>
              <EditParameter toggleModal={toggleModal} groupName="Group" columns={columns} />
            </div>
          </Dimmer>
        )}
      </div>
    );
  }

  /**
   * Returns status if parameter has been selected
   * @param {Api.AnyParameter} fault
   * @returns {boolean}
   */
  private isParameterSelected = (parameter: Api.AnyParameter): boolean => {
    const editedObjectId = this.props.editedObjectId ?? this.props.mainState.lastEditedObjectId;
    if (editedObjectId && parameter.id === editedObjectId) {
      return true;
    } else {
      return false;
    }
  };
}

export default GroupParametersTable;
